import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../../../store/globalState";
import { useSessionStore } from "../../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  Layout,
  Navbar,
  WalletIcon,
} from "lola-framework-ui-test";
import { APP_ROUTES, flavorClassName } from "../../../constants";
import { FlagCircle } from "../../../components/FlagCircle";
import { FirstSendWarning } from "../../../components/FirstSendWarning";
import { useSendCooldown } from "../../../hooks/useSendCooldown";
import { QuoteInfo } from "./components/QuoteInfo";
import { amountFormatter } from "../../../utils";
import { ExchangeFeeInfo } from "./components/ExchangeFeeInfo";
import { PayoutInfo } from "./components/PayoutInfo";
import { Popup } from "../../../components/Popup";
import { SelectPaymentMethodSlot } from "./components/SelectPaymentMethodSlot";
import { DestinationAccountInfo } from "./components/DestinationAccountInfo";
import { paymentType } from "../../../interfaces/payment";
import masterCardLogo from "../../../assets/master_card_icon.png";
import visaLogo from "../../../assets/visa_color_icon.png";
import { useQuote } from "../../../hooks/useQuote";
import { COUNTRY_CURRENCY } from "../../../hooks/useExchangeRate";
import { checkCipReadiness, getAllPaymentMethods } from "../../../services/api";
import { getRouteAfterCipGate } from "../../../utils/phoneNumberFlow";
import { hasCompletedCip } from "../../../utils/cipFlow";
import { config } from "../../../config";
import { NavbarBackChevron } from "../../../components/NavbarBackChevron";

/**
 * Map widget payment types to API payment method types
 */
const mapPaymentTypeToMethod = (
  type: paymentType | undefined
): string | null => {
  if (!type) return null;
  switch (type) {
    case "CARD":
      return "card";
    case "BANK":
      return "bank_account";
    case "CASH":
      return "cash";
    default:
      return null;
  }
};

export const TransferPage = memo(() => {
  const { t } = useTranslation();
  const { theme, amountState, paymentState, setPaymentState, setQuoteState, cardList } = useStore();
  const { session } = useSessionStore();
  const { amount } = amountState;
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    buttonShowIcon,
    buttonSize,
  } = colorConfig;
  const navigate = useNavigate();
  const cooldownSeconds = useSendCooldown();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);
  const [cipLoading, setCipLoading] = useState(false);

  const allowedFundingMethods = session.data?.config?.allowedFundingMethods;
  const allowedMethods = useMemo(
    () => allowedFundingMethods ?? [],
    [allowedFundingMethods]
  );
  const cipAlreadyDone = hasCompletedCip(session.data);

  // Auto-select last used funding method on mount
  useEffect(() => {
    // Read from the store so the effect doesn't re-run on every payment change
    // and the post-await check sees the latest value instead of a stale closure.
    if (useStore.getState().paymentState.payment) return;
    const linkId = session.data?.linkId;
    if (!linkId) return;

    getAllPaymentMethods(linkId)
      .then((methods) => {
        if (useStore.getState().paymentState.payment) return;
        const lastUsed = methods.lastUsedFunding;
        if (!lastUsed) return;

        if (lastUsed.method === "cash" && allowedMethods.includes("cash")) {
          setPaymentState({
            payment: {
              cardId: null,
              type: paymentType.CASH,
              brand: t("payment.cashDeposit"),
            },
          });
        } else if (lastUsed.method === "card" && lastUsed.paymentMethodId && allowedMethods.includes("card")) {
          const card = methods.cards.find(
            (c) => c.enabled && c.paymentMethodId === lastUsed.paymentMethodId
          );
          if (card) {
            setPaymentState({
              payment: {
                cardId: card.paymentMethodId,
                type: paymentType.CARD,
                brand: `${card.brand}-${card.last4}`,
              },
            });
          }
        }
      })
      .catch(() => {});
  }, [session.data?.linkId, cardList, allowedMethods, setPaymentState, t]);

  // Get config from session
  const sourceCountry = session.data?.config?.sourceCountry || "US";
  const destinationCountry = session.data?.config?.destinationCountry || "MX";
  const sourceCurrency = COUNTRY_CURRENCY[sourceCountry] || "USD";
  const destinationCurrency = COUNTRY_CURRENCY[destinationCountry] || "MXN";

  // Map payment type to funding method, default to 'card' for quote preview
  const fundingMethod =
    mapPaymentTypeToMethod(paymentState.payment?.type) || "card";

  // Get cardId from payment state (only for card payments)
  const cardId =
    paymentState.payment?.type === "CARD"
      ? paymentState.payment?.cardId
      : undefined;

  // Fetch quote from API (cardId is signed in the quote to prevent card swapping)
  const { quote, loading: quoteLoading, error: quoteError } = useQuote(
    sourceCountry,
    destinationCountry,
    fundingMethod,
    "bank_account", // Default payout method
    amount ? parseFloat(amount) : null,
    sourceCurrency,
    cardId, // Include cardId in the quote for card payments
    { debounceMs: config.QUOTE_DEBOUNCE_MS }
  );

  useEffect(() => {
    if (quoteError) {
      navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
    }
  }, [quoteError, navigate]);

  // Handle click on confirm button - check CIP readiness before proceeding
  const handleConfirmClick = useCallback(async () => {
    if (!paymentState.payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (!amount || !quote) {
      return;
    }

    const linkId = session.data?.linkId;
    if (!linkId) {
      console.error("[TransferPage] No linkId found in session");
      return;
    }

    // DEBUG: Log quote info before storing
    const expiresAt = new Date(quote.expiresAt).getTime();
    const now = Date.now();
    const remainingMs = expiresAt - now;
    console.log("[TransferPage] Storing quote before navigation:", {
      quoteId: quote.quoteId,
      expiresAt: quote.expiresAt,
      remainingSeconds: Math.floor(remainingMs / 1000),
      isExpired: remainingMs <= 0,
      quoteLoading,
    });

    // Store quote in global state before navigating
    setQuoteState(quote);

    const nextRoute = getRouteAfterCipGate(
      session.data?.hasPhoneNumber,
      useStore.getState().phonePromptSkipped
    );

    // CIP already resolved (backend status) or completed earlier in this
    // session: skip the readiness call, which would otherwise mint a new
    // Kentucky ticket and send the user through CIP again.
    if (cipAlreadyDone) {
      console.log("[TransferPage] CIP already completed, skipping readiness");
      navigate(nextRoute, { replace: true });
      return;
    }

    // Check CIP readiness before proceeding to transfer
    try {
      setCipLoading(true);
      console.log("[TransferPage] Checking CIP readiness...");
      const cipResult = await checkCipReadiness(linkId);
      console.log("[TransferPage] CIP readiness result:", cipResult);

      if (cipResult.shouldRunCip && cipResult.cipTicketId) {
        // User needs to complete CIP - navigate to embedded Kentucky SDK page
        console.log(
          "[TransferPage] CIP required, navigating to CIP page with ticketId:",
          cipResult.cipTicketId
        );
        navigate(APP_ROUTES.CIP_PAGE, {
          replace: true,
          state: { cipTicketId: cipResult.cipTicketId },
        });
        return;
      }

      // No CIP needed - capture phone unless the user already has one
      navigate(nextRoute, { replace: true });
    } catch (err) {
      console.error("[TransferPage] CIP readiness check failed:", err);
      // On error, navigate to error page
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("transfer.error"),
          subTitle: t("transfer.errorSubtitle", "Something went wrong"),
          message:
            err instanceof Error
              ? err.message
              : "Failed to check CIP readiness",
          btnText: t("transfer.tryAgain", "Try again"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    } finally {
      setCipLoading(false);
    }
  }, [
    paymentState.payment,
    amount,
    quote,
    session.data?.linkId,
    session.data?.hasPhoneNumber,
    cipAlreadyDone,
    setQuoteState,
    navigate,
    quoteLoading,
    t,
  ]);

  // Use quote values or defaults
  const exchangeRate = quote?.exchangeRate ?? 0;
  const fee = quote?.fee ?? 0;

  // Calculate display values from quote
  const amountToSend = useMemo(() => {
    if (quoteLoading) return "...";
    if (quote?.receiveAmount) {
      return amountFormatter(quote.receiveAmount, destinationCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [quote, quoteLoading, destinationCurrency]);

  const totalPayment = useMemo(() => {
    if (quote?.totalCost) {
      return amountFormatter(quote.totalCost);
    }
    if (amount) {
      return amountFormatter(parseFloat(amount) + fee);
    }
    return "0";
  }, [quote, amount, fee]);

  const feeDisplay = useMemo(() => {
    return `$${fee.toFixed(2)}`;
  }, [fee]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case "CARD": {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case "BANK":
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case "CASH":
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t]
  );

  const payoutDisplay = useMemo(() => {
    const pm = session.data?.userData?.paymentMethod;
    if (!pm) {
      return {
        subtitle: t("transfer.bankAccount", { last4: "****" }),
        icon: <BankIcon size={20} />,
      };
    }
    if (pm.type === "bank_account" && pm.bankAccount) {
      const label = pm.bankAccount.bankName
        ? `${pm.bankAccount.bankName} ${pm.bankAccount.accountNumber}`
        : pm.bankAccount.accountNumber;
      return { subtitle: label, icon: <BankIcon size={20} /> };
    }
    if (pm.type === "card" && pm.card) {
      const brand = pm.card.brand?.toUpperCase() || "";
      return {
        subtitle: `${brand} ****${pm.card.last4}`.trim(),
        icon: <WalletIcon size={20} />,
      };
    }
    if (pm.type === "cash") {
      const label = pm.cash?.provider || t("transfer.cashPickup", "Cash pickup");
      return { subtitle: label, icon: <CashSpecialIcon size={20} /> };
    }
    return {
      subtitle: t("transfer.bankAccount", { last4: "****" }),
      icon: <BankIcon size={20} />,
    };
  }, [session.data?.userData?.paymentMethod, t]);

  return (
    <>
      <AuraLayout 
        colorConfig={colorConfig}
        className={`scrollable ${flavorClassName}`}
      >
        <Layout.Header>
          <NavbarBackChevron onBackClick={() => navigate(-1)}>
            <Navbar
              title={t("transfer.title")}
              color={title}
              align="center"
              onBackClick={() => navigate(-1)}
            />
          </NavbarBackChevron>
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <section>
            <section className="estimated-quote-section">
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.from")}
                  currency={sourceCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  isActive
                  icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                />
                <QuoteInfo
                  amount={amountToSend || undefined}
                  label={t("transfer.to")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                />
                <ExchangeFeeInfo
                  fee={quoteLoading ? "..." : exchangeRate.toFixed(4)}
                  currency={destinationCurrency}
                />
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payingWith")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowPaymentPopup(true)}
                className={`${
                  !paymentState.payment ? "payment-required" : ""
                } ${shakePayment ? "shake" : ""}`.trim()}
              />
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutDisplay.subtitle}
                icon={payoutDisplay.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowDestinationPopup(true)}
              />
              <FirstSendWarning amount={amount} sourceCurrency={sourceCurrency} />
              <p className="footer-text foreground">
                <b>{t("transfer.instantTransfer")}</b>
                <br />
                {t("transfer.mayTakeFew")}
              </p>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <section className="footer-summary">
              <p className="footer-text foreground">
                {t("transfer.fee", {
                  amount: feeDisplay,
                  currency: sourceCurrency,
                })}
              </p>
              <p className="footer-text foreground">
                {t("transfer.totalPayment", {
                  amount: `$${totalPayment}`,
                  currency: sourceCurrency,
                })}
              </p>
            </section>
            <div
              className={`button-wrapper ${
                amount &&
                paymentState.payment &&
                quote &&
                !quoteLoading &&
                !cipLoading
                  ? "rainbow-ready"
                  : ""
              }`}
            >
              <Button
                size={buttonSize ?? "small"}
                showIcon={buttonShowIcon}
                background={backgroundBtn}
                color={textColorBtn}
                disabled={
                  !amount ||
                  !quote ||
                  quoteLoading ||
                  cipLoading ||
                  cooldownSeconds > 0
                }
                onClick={handleConfirmClick}
              >
                {cooldownSeconds > 0
                  ? t("transfer.retryIn", { seconds: cooldownSeconds })
                  : cipLoading
                  ? t("transfer.verifying", "Verifying...")
                  : quoteLoading
                  ? t("transfer.loadingQuote", "Loading...")
                  : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
      >
        <SelectPaymentMethodSlot onClose={() => setShowPaymentPopup(false)} />
      </Popup>
      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <DestinationAccountInfo
          onClose={() => setShowDestinationPopup(false)}
        />
      </Popup>
    </>
  );
});
